import { Box, Typography, useTheme } from '@mui/material'
import { MUIThemeColors } from 'src/mui-theme'
import { getFormattedDate, getThemeColor } from 'src/utils'
import { GridColumns } from '@mui/x-data-grid-pro'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'
import { IProductCertification, ProductCertificationFilters } from 'src/generated-types/product-certification'
import { productCertificateQueryKeys } from 'src/query-keys'
import { productCertificationsEndpoints } from 'src/api/product-certification'
import debounce from 'lodash/debounce'
import { FilterHeader } from 'src/containers/Equipment/FilterHeader'
import { StyledInfiniteScrollTable } from 'src/containers/Equipment/EquipmentListContainer'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { useRowCount } from 'src/hooks/useViewportHeight'
import { toast } from 'react-toastify'

interface CertificateTableProps {
  customerId: EntityId
  headerFilters: ProductCertificationFilters
  setHeaderFilters: React.Dispatch<React.SetStateAction<ProductCertificationFilters>>
}
const CertificateTable: React.FC<CertificateTableProps> = ({ customerId, headerFilters, setHeaderFilters }) => {
  const theme = useTheme()
  const { t } = useTranslation(['common'])
  const rowCount = useRowCount()
  const filterInputRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const { openFilePreviewDialog } = useDialogCtx()

  // Mirror headerFilters into a ref so updateFilter/clearFilter/createFilterHeader can
  // read the latest value without listing headerFilters as a dependency. Without this,
  // every debounced state update rebuilt the columns array and remounted the filter
  // inputs, causing them to lose focus mid-typing.
  const headerFiltersRef = useRef(headerFilters)
  useEffect(() => {
    headerFiltersRef.current = headerFilters
  }, [headerFilters])

  const debouncedServerFilter = useMemo(
    () =>
      debounce((filters: ProductCertificationFilters) => {
        setHeaderFilters(filters)
      }, 500),
    [setHeaderFilters],
  )

  useEffect(() => {
    return () => {
      debouncedServerFilter.cancel()
    }
  }, [debouncedServerFilter])

  const updateFilter = useCallback(
    (field: string, value: string | number | number[] | string[]) => {
      const updatedLocalFilters = { ...headerFiltersRef.current }

      if (!value || (Array.isArray(value) && value.length === 0) || (typeof value === 'string' && !value.trim())) {
        delete updatedLocalFilters[field]
      } else {
        updatedLocalFilters[field] = value
      }

      debouncedServerFilter(updatedLocalFilters)
    },
    [debouncedServerFilter],
  )

  const clearFilter = useCallback(
    (field: string) => {
      const updatedLocalFilters = { ...headerFiltersRef.current }
      delete updatedLocalFilters[field]

      debouncedServerFilter(updatedLocalFilters)
    },
    [debouncedServerFilter],
  )

  const createFilterHeader = useCallback(
    (
      headerName: string,
      field: string,
      filterType: 'text' | 'number' | 'date' | 'dropdown' = 'text',
      options?: {
        placeholder?: string
        dropdownOptions?: Array<{ value: string | number; label: string }>
        loading?: boolean
        multiple?: boolean
      },
    ) => {
      return (
        <FilterHeader
          headerName={headerName}
          field={field}
          filterType={filterType}
          placeholder={options?.placeholder}
          dropdownOptions={options?.dropdownOptions}
          loading={options?.loading}
          multiple={options?.multiple}
          filters={headerFiltersRef.current}
          filterInputRefs={filterInputRefs}
          updateFilter={updateFilter}
          clearFilter={clearFilter}
        />
      )
    },
    [updateFilter, clearFilter],
  )

  const queryParams = useMemo(
    () =>
      ({
        productNumber: headerFilters.productNumber ?? '',
        productName: headerFilters.productName ?? '',
        supplier: headerFilters.supplier ?? '',
        batchNumbers: headerFilters.batchNumbers ?? '',
      } as ProductCertificationFilters),
    [headerFilters],
  )

  const onClick = (fileUrl: string) => {
    if (fileUrl) {
      openFilePreviewDialog(fileUrl)
    } else {
      toast.error(t('file-not-found', { ns: 'product' }))
    }
  }

  const StaticColumns: GridColumns = useMemo(
    () => [
      {
        field: 'productNumber',
        headerName: t('productNumber', { ns: 'component' }),
        renderCell: ({ row }) => {
          return row.productNumber
        },
        flex: 1,
        cellClassName: 'font-weight-semi-bold',
        renderHeader: () => createFilterHeader(t('productNumber', { ns: 'component' }), 'productNumber'),
      },
      {
        field: 'productName',
        headerName: t('product-name', { ns: 'component' }),
        flex: 1.5,
        renderCell: ({ row }) => {
          return <DynamicTextTypography text={row.productDescription} fontWeight={400} maxLength={45} />
        },
        renderHeader: () => createFilterHeader(t('product-name', { ns: 'component' }), 'productName'),
        cellClassName: 'font-weight-semi-bold',
      },
      {
        field: 'supplier',
        headerName: t('product-supplier', { ns: 'component' }),
        flex: 1,
        renderCell: ({ row }) => {
          return <DynamicTextTypography text={row.supplierName} fontWeight={400} maxLength={35} />
        },
        renderHeader: () => createFilterHeader(t('product-supplier', { ns: 'component' }), 'supplier'),
        cellClassName: 'font-weight-semi-bold',
      },
      {
        field: 'batchNumber',
        headerName: t('tracking', { ns: 'component' }),
        flex: 1,
        renderCell: ({ row }) => {
          return <DynamicTextTypography text={row.batchNumbers?.replace(/,/g, ', ')} fontWeight={400} maxLength={35} />
        },
        renderHeader: () => createFilterHeader(t('tracking', { ns: 'component' }), 'batchNumbers'),
        cellClassName: 'font-weight-semi-bold',
      },
      {
        field: 'createdAt',
        headerName: t('registered', { ns: 'common', defaultValue: 'Registered' }),
        flex: 0.8,
        renderCell: ({ row }) => {
          const raw = row.createdAt
          const date = raw ? new Date(raw) : null
          const isValidDate = date && date.getFullYear() > 2000
          const userName =
            row.createdByFirstName || row.createdByLastName
              ? `${row.createdByFirstName || ''} ${row.createdByLastName || ''}`.trim()
              : ''
          if (!isValidDate && !userName) return null
          return (
            <Box>
              {isValidDate && (
                <Typography variant="body2" fontWeight={400} fontSize={13}>
                  {getFormattedDate(date)}
                </Typography>
              )}
              {userName && (
                <Typography variant="caption" color="text.secondary" fontSize={11}>
                  {userName}
                </Typography>
              )}
            </Box>
          )
        },
        sortable: false,
      },
    ],
    [t, createFilterHeader],
  )
  return (
    <Box
      padding={'8px'}
      bgcolor={getThemeColor(theme, MUIThemeColors.white)}
      borderRadius={'4px'}
      height={window.innerHeight - 220}
    >
      <StyledInfiniteScrollTable<IProductCertification>
        id={'product-certification-table'}
        queryUrl={productCertificationsEndpoints.getProductCertificates()}
        queryParams={queryParams}
        generateQueryKey={(page: number, pageSize: number) =>
          productCertificateQueryKeys.GET_PRODUCT_CERTIFICATES(customerId as EntityId, {
            ...queryParams,
            page,
            pageSize,
          })
        }
        pageSize={rowCount}
        columns={StaticColumns}
        autoHeight={false}
        height={window.innerHeight - 240}
        getRowHeight={() => 36}
        minRowHeight="36px"
        containerProps={{ sx: { overflowX: 'hidden' } }}
        hideDefaultFooterPagination
        onRowClick={(params, event) => {
          event.stopPropagation()
          const row = params.row as IProductCertification
          onClick(row.fileUrl)
        }}
      />
    </Box>
  )
}

export default CertificateTable
